import { useCallback, useEffect, useRef, useState } from "react";

import {
  createZone,
  deleteZone,
  getLatestDataPoints,
  listBuildingFindings,
  listEquipment,
  listZones,
  updateZone,
  type DataPointSample,
  type Equipment,
  type FindingSeverity,
  type Zone as ApiZone,
} from "@/lib/api/building";
import {
  getKBDataPointLabels,
  getKBTypes,
  type KBZoneSummary,
} from "@/lib/api/agent";
import { resolvedEquipmentZoneIds, type Zone } from "@/lib/building-domain";

import { ZoneGrid } from "./ZoneGrid";
import { zoneSignalSeries } from "./zone-live-signals";

interface Props {
  buildingId: string;
  operator?: boolean;
  refreshKey?: number;
  onChanged?: () => void;
}

function to_zone(z: ApiZone): Zone {
  return {
    id: z.id,
    building_id: z.building_id,
    name: z.name,
    floor: z.floor,
    icon: z.icon,
    zone_type: z.zone_type,
    description: z.description,
    updated_at: z.updated_at,
  };
}

export function ZonesView({
  buildingId,
  operator = false,
  refreshKey = 0,
  onChanged,
}: Props) {
  const [zones, set_zones] = useState<Zone[]>([]);
  const [zone_equipment, set_zone_equipment] = useState<
    Map<string, Equipment[]>
  >(new Map());
  const [severity_map, set_severity_map] = useState<
    Map<string, FindingSeverity>
  >(new Map());
  const [kb_zone_types, set_kb_zone_types] = useState<KBZoneSummary[]>([]);
  const [live_signals, set_live_signals] = useState<
    Map<string, Record<string, DataPointSample[]>>
  >(new Map());
  const [kb_labels, set_kb_labels] = useState<Record<string, string>>({});
  const [loading, set_loading] = useState(true);
  // Guards against overlapping load() calls (StrictMode's dev double-invoke, or a fast
  // refreshKey bump racing the initial mount) committing a superseded response — the older
  // call would otherwise still set_zones with a stale-but-different array reference, which
  // re-fires every effect downstream that depends on `zones` for no real data change.
  const load_token = useRef(0);

  const load = useCallback(async () => {
    const token = ++load_token.current;
    // Equipment cards wait for list + live, then paint complete. Zone cards used
    // to list first, mount, then wait on listDataPointSources (KB enrich per type,
    // ~10× slower than latest-points) before showing CO₂. Fetch latest + the one
    // KB label map with the zone list so the grid appears already filled.
    const zones_p = listZones(buildingId);
    const equipment_p = listEquipment(buildingId);
    const labels_p = getKBDataPointLabels().catch(
      (): Record<string, string> => ({}),
    );
    const latest_p = zones_p.then((raw) => {
      if (raw.length === 0) return [] as DataPointSample[];
      return getLatestDataPoints(
        buildingId,
        raw.map((z) => z.id),
      ).catch((): DataPointSample[] => []);
    });
    const [raw_zones, raw_equipment, labels, samples] = await Promise.all([
      zones_p,
      equipment_p,
      labels_p,
      latest_p,
    ]);
    if (load_token.current !== token) return; // a newer load() has since started
    set_zones(raw_zones.map(to_zone));
    const grouped = new Map<string, Equipment[]>();
    for (const e of raw_equipment) {
      for (const zoneId of resolvedEquipmentZoneIds(e)) {
        const existing = grouped.get(zoneId) ?? [];
        grouped.set(zoneId, [...existing, e]);
      }
    }
    set_zone_equipment(grouped);
    const live = new Map<string, Record<string, DataPointSample[]>>();
    for (const z of raw_zones) {
      live.set(z.id, zoneSignalSeries(z.id, samples));
    }
    set_live_signals(live);
    set_kb_labels(labels);
    set_loading(false);
  }, [buildingId]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  useEffect(() => {
    getKBTypes()
      .then((r) => set_kb_zone_types(r.zone_types))
      .catch(() => {});
  }, []);

  useEffect(() => {
    listBuildingFindings(buildingId, { status: "open" })
      .then((findings) => {
        const m = new Map<string, FindingSeverity>();
        for (const f of findings) {
          const prev = m.get(f.source_id);
          if (
            !prev ||
            f.severity === "critical" ||
            (f.severity === "warning" && prev === "info")
          ) {
            m.set(f.source_id, f.severity);
          }
        }
        set_severity_map(m);
      })
      .catch(() => {});
  }, [buildingId]);

  async function handle_create_zone(data: {
    name: string;
    floor: number;
    zone_type: string | null;
    description: string;
  }) {
    await createZone(buildingId, data);
    await load();
    onChanged?.();
  }

  async function handle_update_zone(
    zone_id: string,
    data: {
      name?: string;
      floor?: number;
      icon?: string;
      zone_type?: string | null;
      description?: string;
    },
  ) {
    await updateZone(buildingId, zone_id, data);
    await load();
    onChanged?.();
  }

  async function handle_delete_zone(zone_id: string) {
    await deleteZone(buildingId, zone_id);
    await load();
    onChanged?.();
  }

  if (loading) {
    return (
      <p className="mt-8 text-center text-sm text-muted-foreground">
        Ladataan...
      </p>
    );
  }

  return (
    <ZoneGrid
      buildingId={buildingId}
      zones={zones}
      zone_equipment={zone_equipment}
      kb_zone_types={kb_zone_types}
      live_signals={live_signals}
      kb_labels={kb_labels}
      operator={operator}
      on_create={handle_create_zone}
      on_update={handle_update_zone}
      on_delete={handle_delete_zone}
      findings_severity={severity_map}
    />
  );
}
